import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/lib/icons";
import { toast } from "sonner";
import { LogOut, Crown, Flame, Shield, BookmarkCheck, ExternalLink, Zap } from "lucide-react";
import { SubscriptionManager } from "@/components/SubscriptionManager";

const TYPE_ICON = { lesson: "BookOpen", term: "BookA", resource: "BookMarked" };

export default function Account() {
  const { user, logout, checkAuth } = useAuth();
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);
  const isPro = user?.is_pro || user?.tier === "pro";
  const isAdmin = user?.role === "admin";

  useEffect(() => { api.get("/saved").then((r) => setSaved(r.data)).catch(() => {}); }, []);

  const toggleTier = async (tier) => {
    setBusy(true);
    try {
      await api.post("/admin/toggle-tier", { tier });
      await checkAuth();
      toast.success(`Switched to ${tier === "pro" ? "Pro" : "Free"} tier`);
    } catch (e) { toast.error("Could not switch tier"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="account-page">
      <div>
        <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Account</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Your profile</h1>
      </div>

      <div className="eco-card p-6 flex items-center gap-5">
        <div className="h-16 w-16 rounded-2xl bg-white/10 flex items-center justify-center overflow-hidden shrink-0">
          {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : <span className="text-2xl font-display font-bold text-white">{(user?.name || "U")[0]}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-display text-xl font-semibold text-white truncate">{user?.name}</h2>
            {isPro ? <span className="eco-tag flex items-center gap-1 text-amber-300 border-amber-400/20 bg-amber-400/10"><Crown size={12} /> PRO</span> : <span className="eco-tag">FREE</span>}
            {isAdmin && <span className="eco-tag flex items-center gap-1 text-[#4d8bff] border-[#0055FF]/30 bg-[#0055FF]/10"><Shield size={12} /> ADMIN</span>}
          </div>
          <p className="text-sm text-slate-400 truncate">{user?.email}</p>
          <p className="text-xs text-slate-600 mt-1">Signed in via {user?.auth_provider === "google" ? "Google" : "email"}</p>
        </div>
        <div className="text-center hidden sm:block">
          <div className="flex items-center gap-1.5 text-amber-400"><Flame size={18} /><span className="font-display text-2xl font-bold text-white">{user?.streak ?? 0}</span></div>
          <div className="text-[10px] font-mono-eco uppercase tracking-widest text-slate-500 mt-1">Day streak</div>
        </div>
      </div>

      {/* Admin tier switcher */}
      {isAdmin && (
        <div className="eco-card p-6" data-testid="admin-tier-switch">
          <div className="flex items-center gap-2 mb-1"><Shield size={16} className="text-[#4d8bff]" /><h3 className="font-display font-semibold text-white">Admin: switch tier</h3></div>
          <p className="text-sm text-slate-400 mb-4">Toggle your own account between Free and Pro to test the difference.</p>
          <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-full p-1">
            <button data-testid="admin-set-free" disabled={busy} onClick={() => toggleTier("free")} className={`text-sm font-medium px-5 py-2 rounded-full transition-all ${!isPro ? "bg-white text-black" : "text-slate-400"}`}>Free</button>
            <button data-testid="admin-set-pro" disabled={busy} onClick={() => toggleTier("pro")} className={`text-sm font-medium px-5 py-2 rounded-full transition-all flex items-center gap-1.5 ${isPro ? "bg-[#0055FF] text-white" : "text-slate-400"}`}><Crown size={13} /> Pro</button>
          </div>
        </div>
      )}

      {/* Subscription */}
      {!isAdmin && <SubscriptionManager />}

      {/* Saved */}
      <div>
        <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white mb-4"><BookmarkCheck size={18} className="text-slate-500" /> Saved library ({saved.length})</h3>
        {saved.length === 0 ? (
          <p className="text-sm text-slate-500">Nothing saved yet. Save lessons, terms and resources to find them here.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {saved.map((s) => (
              <div key={s.id} className="eco-card p-4 flex items-center gap-3" data-testid={`saved-${s.id}`}>
                <Icon name={TYPE_ICON[s.type] || "Bookmark"} size={18} className="text-[#4d8bff] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-white truncate">{s.title}</div>
                  <div className="text-xs text-slate-500 capitalize">{s.type}</div>
                </div>
                {s.meta?.url && <a href={s.meta.url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white"><ExternalLink size={15} /></a>}
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={logout} data-testid="logout-btn" className="btn-secondary"><LogOut size={16} /> Sign out</button>

      <div className="pt-6 border-t border-white/5 flex flex-wrap items-center gap-5 text-xs text-slate-600">
        <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
        <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
        <a href="mailto:ecoentrepreneuros@gmail.com" className="hover:text-white transition-colors">Contact support</a>
      </div>
    </div>
  );
}
